import React, { useMemo, useContext, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
	FaUserTie,
	FaClock,
	FaBookmark,
	FaHotel,
	FaBusinessTime,
	FaSpinner,
} from "react-icons/fa";
import { FaRegBookmark, FaGraduationCap, FaArrowUpRightFromSquare } from "react-icons/fa6";
import { BiMaleFemale } from "react-icons/bi";
import { MdLocationPin } from "react-icons/md";
import { toast } from 'react-toastify';
import AuthContext from "../../../../store/AuthContext";
import { BASE_URL_API } from "../../../../References/urls";
import styles from "./JobCard.module.css";

const JobCard = ({ job, isBookmarked, onToggleBookmark, onRemove }) => {
	const authCtx = useContext(AuthContext);
	const navigate = useNavigate();
	const [isSavingBookmark, setIsSavingBookmark] = useState(false);
  // Calculate how many days ago posted
  const getPostedDaysAgo = (postedOn) => {
    if (!postedOn) return "";
    const postDate = new Date(postedOn);
    const now = new Date();
    const diffTime = Math.abs(now - postDate);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "1 day ago";
    return `${diffDays} days ago`;
  };

  const firstLetter = useMemo(() => {
    const name = (job?.jobTitle || "").trim();
    return name ? name[0].toUpperCase() : "J";
  }, [job?.jobTitle]);

  const handleSaveJob = async () => {
  	if (!authCtx.isLoggedIn) {
  		navigate('/login');
  		return;
  	}
 
  	if (isSavingBookmark) return;
 
  	setIsSavingBookmark(true);
  	try {
      const response = await fetch(BASE_URL_API + "/saveJob", {
        method: "POST",
        body: JSON.stringify({
         userIdentifier: authCtx.userIdentifier,
  				token: authCtx.token,
  				jobIdentifier: job?.jobIdentifier,
        }),
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const data = await response.json();
      if (data.status === "success") {
        if (data.message === "successfully added the job to saved list") {
          onToggleBookmark();
          toast.success("Job saved successfully.");
        } else if (data.message === "successfully removed the job from saved list") {
          onToggleBookmark();
          if (onRemove) onRemove(job.jobIdentifier);
          toast.success("Job removed successfully.");
        }
      } else {
        toast.error("Failed to save job: " + (data.message || "Unknown error"));
      }
    } catch (error) {
    	console.error("Error saving job:", error);
    	toast.error("An error occurred while saving the job. Please try again.");
    } finally {
    	setIsSavingBookmark(false);
    }
   };

  return (
    <Link to={`/job/${job?.jobRoute}`} className={styles.jobCard}>
      {/* Card Header */}
      <div className={styles.cardHeader}>
        <div className={styles.jobIcon}>
          <span>{firstLetter}</span>
        </div>

        <div className={styles.titleWrapper}>
          <h2 className={styles.jobTitle}>{job?.jobTitle || "Untitled Job"}</h2>
          <p className={styles.companyName}>
            <span className={styles.companyText}>
              {job?.storeName || "Unknown Company"}
            </span>
          </p>
          <div className={styles.locationDays}>
            <p className={styles.companyAddress}>
              <MdLocationPin className={styles.locationIcon} />
              {job?.shortenAddress || job?.storeAddress}
            </p>
            <span className={styles.postedOn}>
              <FaClock style={{ color: "#86868b" }} />
              {getPostedDaysAgo(job?.postedOn)}
            </span>
          </div>
        </div>
        <div
        	className={styles.bookmarkIcon}
        	onClick={(e) => {
        		e.preventDefault(); // stop Link navigation
        		e.stopPropagation();
        		handleSaveJob();
        	}}
        >
        	{isSavingBookmark ? (
        		<FaSpinner className={styles.spinner} />
        	) : isBookmarked ? (
        		<FaBookmark style={{ color: "#da1884" }} />
        	) : (
        		<FaRegBookmark style={{ color: "#da1884" }} />
        	)}
        </div>
      </div>

      <div className={styles.infoGrid}>
        <div className={styles.infoItem}>
          <FaBusinessTime className={styles.icon} />
          <span className={styles.infoTitle}>Full Time</span>
        </div>
        {job?.whoCanApply && (
          <div className={styles.infoItem}>
            <BiMaleFemale className={styles.icon} />
            <span className={styles.infoTitle}>{job.whoCanApply}</span>
          </div>
        )}
        {job?.educationQualifications && (
          <div className={styles.infoItem}>
            <FaGraduationCap className={styles.icon} />
            <span className={styles.infoTitle}>
              {job.educationQualifications}
            </span>
          </div>
        )}
        {job?.experienceRequired && (
          <div className={styles.infoItem}>
            <FaUserTie className={styles.icon} />
            <span className={styles.infoTitle}>{job.experienceRequired}</span>
          </div>
        )}
        {job?.fna === "None" ? (
          <div className={styles.infoItemNone}>
            <FaHotel className={styles.iconNone} />
            <span className={styles.infoTitleNone}>No Accommodation</span>
          </div>
        ) : (
          <div className={styles.infoItem}>
            <FaHotel className={styles.icon} />
            <span className={styles.infoTitle}>{job.fna}</span>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className={styles.cardFooter}>
        <span className={styles.salary}>
          ₹{job.minSalary} - ₹{job.maxSalary}
        </span>
        <button className={styles.applyBtn}>
          Apply <FaArrowUpRightFromSquare className={styles.applyBtnIcon} />{" "}
        </button>
      </div>
    </Link>
  );
};

export default JobCard;
